/**
 * Mobile device detection and responsive utilities
 */

/**
 * Detect if the current device is a mobile device
 */
export function isMobileDevice(): boolean {
  // ``window.opera`` (legacy Opera) and ``navigator.msMaxTouchPoints``
  // (IE/old Edge) are non-standard vendor fields absent from lib.dom —
  // narrow structural casts instead of ``any``.
  const userAgent =
    navigator.userAgent ||
    navigator.vendor ||
    (window as Window & { opera?: string }).opera ||
    "";

  // Check for mobile patterns
  const mobileRegex =
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;
  const isMobileUA = mobileRegex.test(userAgent);

  // Check for touch capability
  const hasTouchScreen =
    "ontouchstart" in window ||
    navigator.maxTouchPoints > 0 ||
    ((navigator as Navigator & { msMaxTouchPoints?: number })
      .msMaxTouchPoints ?? 0) > 0;

  // Check screen size
  const isSmallScreen = window.innerWidth <= 768;

  return isMobileUA || (hasTouchScreen && isSmallScreen);
}

/**
 * Detect if the device is specifically a phone (vs tablet)
 */
export function isPhoneDevice(): boolean {
  return isMobileDevice() && window.innerWidth <= 480;
}

/**
 * Detect if the device is a tablet
 */
export function isTabletDevice(): boolean {
  return (
    isMobileDevice() && window.innerWidth > 480 && window.innerWidth <= 1024
  );
}

/**
 * Get the device type
 */
export function getDeviceType(): "desktop" | "tablet" | "phone" {
  if (isPhoneDevice()) return "phone";
  if (isTabletDevice()) return "tablet";
  return "desktop";
}

/**
 * Check if we should use redirect flow instead of popup
 *
 * Popup windows don't work well on mobile:
 * - Browsers often block them
 * - They open as full-screen tabs
 * - Users lose context of parent app
 */
export function shouldUseRedirectFlow(): boolean {
  const deviceType = getDeviceType();

  // Always use redirect for phones
  if (deviceType === "phone") return true;

  // Use redirect for tablets in portrait mode (popups are cramped)
  if (deviceType === "tablet" && window.innerHeight > window.innerWidth) {
    return true;
  }

  // Use popup for desktop and tablets in landscape
  return false;
}
